/**
 * Graphical workflow builder.
 *
 * Replaces the legacy ConnectionsMap / AgentLayout edit pages. Three regions:
 *   - LEFT  palette: every catalog listener, draggable onto the canvas.
 *   - RIGHT palette: every catalog skill, draggable onto the canvas.
 *   - CENTER canvas: an SVG layer for connection curves and an absolutely-
 *     positioned node layer. The agent node lives at the centre; new nodes
 *     auto-arrange around it when first dropped, then can be moved freely.
 *
 * Double-clicking any node opens a tabbed modal. The "Settings" tab edits the
 * node's configuration; the "Code" tab opens the node's source in Monaco.
 *
 * Layout positions are stored on the agent record under `workflow_layout` and
 * persisted with the same /agents/<id> PATCH endpoint the rest of the app uses.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Editor from '@monaco-editor/react';
import {
  Mail, MessageSquare, Globe, Clock, Rss, Webhook, Database,
  Send, Search, FileText, Brain, Sparkles, Settings as SettingsIcon,
  Code2, Activity, Plus, Trash2, X, Save, Loader2,
  Check, AlertCircle, Boxes, ChevronRight, Power, Info,
  KeyRound, Rocket, Plug,
  Calendar, CalendarPlus, Download, FolderUp,
} from 'lucide-react';
import { LISTENER_SETUP_HELP, listenerKind } from '../listener_setup_help.jsx';
import SchedulePanel from '../components/SchedulePanel';
import TopNav from '../components/TopNav';
import { useTheme } from '../contexts/ThemeContext';
import {
  getAgent, updateAgent, fetchCatalog,
  listAgentSkills, installAgentSkill, uninstallAgentSkill,
  setAgentSkillMode, getAgentSkillCredentials, putAgentSkillCredentials,
  readAgentSkillFile, writeAgentSkillFile,
  installListenerFromCatalog, readAgentListenerFile, writeAgentListenerFile,
  updateListenerSpec,
  enableAgentSkill, disableAgentSkill,
  testCatalogConnection, reloadService,
  downloadAgentSkillZip, uploadAgentSkillFolder,
  downloadAgentListenerZip, uploadAgentListenerFolder,
  listAgentSubagents, installAgentSubagent, uninstallAgentSubagent,
} from '../data';
import { SubagentConfig } from './SubagentWorkspace';
import {
  ESCALATION_CHANNELS,
  escalationTargetLabel,
  escalationTargetPlaceholder,
  escalationTargetHint,
} from '../escalation_meta';
import './WorkflowBuilder.css';

// ---------------------------------------------------------------------------
// Icons + visual hints
// ---------------------------------------------------------------------------

const ICONS = {
  email_inbox: Mail, slack_messages: MessageSquare,
  database_read: Database, webhook_incoming: Webhook, scheduled_trigger: Clock,
  rss_feed: Rss, google_calendar: Calendar,
  send_email: Send, slack_send: MessageSquare,
  database_query: Database, http_request: Globe, web_search: Search,
  file_read_write: FileText, heartbeat: Activity,
  create_calendar_event: CalendarPlus, read_calendar: Calendar,
  browser_agent: Globe, mcp_agent: Plug,
};
const iconFor = (id) => ICONS[id] || Sparkles;

// Node geometry. Width/height drive both the canvas placement and the
// connection-line anchor maths below; keep them in sync with CSS.
const NODE_W = 180;
const NODE_H = 72;
const AGENT_W = 200;
const AGENT_H = 96;

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

// User-authored items live in the palette as draggable cards and only acquire
// a canvas position when the user explicitly drops them. defaultLayout skips
// them so creating a custom listener/skill doesn't pop a node onto the canvas.
const isCustomListener = (l) => !!l.isCustom;
const isCustomSkill = (s) => !!(s.is_custom ?? s.isCustom);

function defaultLayout(canvasW, canvasH, listenerObjs, skillObjs) {
  const cx = canvasW / 2 - AGENT_W / 2;
  const cy = canvasH / 2 - AGENT_H / 2;
  const agent = { x: cx, y: cy };

  const placedListeners = listenerObjs.filter(l => !isCustomListener(l));
  const placedSkills = skillObjs.filter(s => !isCustomSkill(s));

  const spread = (count, side) => {
    const usable = Math.max(canvasH - 120, 200);
    const step = count > 1 ? usable / (count + 1) : usable / 2;
    return Array.from({ length: count }, (_, i) => ({
      x: side === 'left' ? 80 : canvasW - NODE_W - 80,
      y: 60 + step * (i + 1) - NODE_H / 2,
    }));
  };

  const lp = spread(placedListeners.length, 'left');
  const sp = spread(placedSkills.length, 'right');
  const listeners = Object.fromEntries(placedListeners.map((l, i) => [l.id, lp[i]]));
  const skills = Object.fromEntries(placedSkills.map((s, i) => [s.id, sp[i]]));
  // Subagents have no catalog and are never auto-placed -- they acquire a
  // position only when dragged from the palette (like custom skills).
  return { agent, listeners, skills, subagents: {} };
}

function ensureLayout(saved, canvasW, canvasH, listenerObjs, skillObjs) {
  const base = defaultLayout(canvasW, canvasH, listenerObjs, skillObjs);
  if (!saved || typeof saved !== 'object') return base;
  return {
    agent: saved.agent || base.agent,
    listeners: { ...base.listeners, ...(saved.listeners || {}) },
    skills: { ...base.skills, ...(saved.skills || {}) },
    subagents: { ...base.subagents, ...(saved.subagents || {}) },
  };
}

// Cubic-bezier path between two anchor points; control points push the curve
// horizontally so it reads as a clean "flow" line.
//
// Edge case: when y1 === y2, the path is a perfectly horizontal line and its
// bounding box has zero height. SVG `<linearGradient>` with the default
// `objectBoundingBox` units degenerates against a zero-area box, so the stroke
// renders nothing (the line "disappears"). Nudging the start by half a pixel
// gives the box a non-zero height and the gradient renders normally; the
// half-pixel offset is invisible against a 2-px stroke with rounded ends.
function curvePath(x1, y1, x2, y2) {
  const dx = Math.max(Math.abs(x2 - x1) * 0.5, 60);
  const y1a = y1 === y2 ? y1 - 0.5 : y1;
  return `M ${x1},${y1a} C ${x1 + dx},${y1a} ${x2 - dx},${y2} ${x2},${y2}`;
}

// Vertical counterpart used by subagents, which attach to the top or bottom of
// the agent according to their position on the canvas.
function verticalCurvePath(x1, y1, x2, y2) {
  const dy = Math.max(Math.abs(y2 - y1) * 0.5, 60);
  const direction = y2 < y1 ? -1 : 1;
  const x1a = x1 === x2 ? x1 - 0.5 : x1;
  return `M ${x1a},${y1} C ${x1a},${y1 + direction * dy} ${x2},${y2 - direction * dy} ${x2},${y2}`;
}

// snake_case the name to derive a slug suitable for filesystem ids.
function slugify(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}

// Default drop position for a newly created custom node — the canvas centre.
function centrePosition(canvas) {
  return {
    x: Math.max(0, canvas.w / 2 - NODE_W / 2),
    y: Math.max(0, canvas.h / 2 - NODE_H / 2),
  };
}

const STUB_LISTENER_COMMON_TOP = `"""Custom listener.

Each line of stdout should be a JSON object the runtime can route. The minimal
shape is:
  {"payload": {...}, "match_conversation_thread_by": [...]}
Include a stable "id" field for events you want deduplicated.

Env vars available inside this script:
  AEGENT_SANDBOX     - path to the agent's sandbox directory
  AEGENT_SETTINGS    - JSON of the listener's settings
  AEGENT_CREATED_AT  - ISO timestamp of when the agent was created
  AEGENT_MODE        - the chosen mode (if any)
  AEGENT_EVENT       - the inbound event (webhook listeners only)
  Anything you save in user_params is also exposed as an env var.
"""`;

function stubListenerCode(kind) {
  if (kind === 'webhook') {
    return `${STUB_LISTENER_COMMON_TOP}
import json
import os


def main():
    raw = os.environ.get("AEGENT_EVENT", "")
    try:
        event = json.loads(raw) if raw else {}
    except (ValueError, TypeError):
        event = {"body": raw}
    print(json.dumps({
        "payload": {"source": "custom_webhook", **event},
        "match_conversation_thread_by": ["source"],
    }))


if __name__ == "__main__":
    main()
`;
  }
  if (kind === 'cron') {
    return `${STUB_LISTENER_COMMON_TOP}
import json
from datetime import datetime, timezone


def main():
    print(json.dumps({
        "payload": {
            "source": "scheduled",
            "trigger_time": datetime.now(timezone.utc).isoformat(),
        },
        "match_conversation_thread_by": ["source"],
    }))


if __name__ == "__main__":
    main()
`;
  }
  // polling (default)
  return `${STUB_LISTENER_COMMON_TOP}
import json


def main():
    # Replace this with your polling logic. Append one dict per real event to
    # \`\`events\`\` and the runtime will route each one. An empty list means
    # nothing happened this tick -- the agent won't be woken up.
    # The "id" field dedups artifacts across ticks: give each real event a
    # stable, source-derived id (e.g. a message id, row primary key, RSS guid).
    events = []
    # Example (uncomment and adapt):
    # events.append({
    #     "id": message_id,
    #     "payload": {"source": "custom", "from": sender, "body": text},
    #     "match_conversation_thread_by": ["from"],
    # })
    print(json.dumps(events))


if __name__ == "__main__":
    main()
`;
}

function stubSkillCode(slug) {
  return `"""Custom skill.

Implement a smolagents Tool. The agent will pick this up automatically once it
is enabled and use it whenever the description below matches what it needs.
"""
import json
import sys

from smolagents import Tool


class CustomTool(Tool):
    name = "${slug}"
    description = "Describe what this skill does so the agent knows when to use it."
    inputs = {
        "value": {"type": "string", "description": "What to operate on"},
    }
    output_type = "string"

    def forward(self, value):
        # Replace with your real logic.
        return f"You sent: {value}"


if __name__ == "__main__":
    kwargs = json.loads(sys.argv[1]) if len(sys.argv) > 1 else {}
    result = CustomTool().forward(**kwargs)
    print(result if isinstance(result, str) else json.dumps(result))
`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function WorkflowBuilder() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const [agent, setAgent] = useState(null);
  const [catalog, setCatalog] = useState({ listeners: [], skills: [], subagents: [] });
  const [skills, setSkills] = useState([]); // installed skills (server-side)
  const [subagents, setSubagents] = useState([]); // installed subagent blocks
  // agent starts as null so ensureLayout's "saved.agent || base.agent" fallback
  // actually fires; once the canvas is measured we drop the agent at its centre.
  const [layout, setLayout] = useState({ agent: null, listeners: {}, skills: {}, subagents: {} });
  const [canvasSize, setCanvasSize] = useState({ w: 1000, h: 700 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // {kind, id, action}
  const [openNode, setOpenNode] = useState(null); // {kind:'listener'|'skill'|'agent', id, data}
  const [hoverEdge, setHoverEdge] = useState(null);
  const [customKind, setCustomKind] = useState(null); // 'listener' | 'skill' | null

  const canvasRef = useRef(null);
  const draggingRef = useRef(null); // {kind, id, offsetX, offsetY}

  // ----- initial load ------------------------------------------------------

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, c, sk, sa] = await Promise.all([
          getAgent(agentId),
          fetchCatalog().catch(() => ({ listeners: [], skills: [], subagents: [] })),
          listAgentSkills(agentId).catch(() => []),
          listAgentSubagents(agentId).catch(() => []),
        ]);
        if (!alive) return;
        setAgent(a);
        setCatalog(c);
        setSkills(sk);
        setSubagents(sa);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [agentId]);

  // ----- canvas sizing -----------------------------------------------------

  useEffect(() => {
    const measure = () => {
      const el = canvasRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCanvasSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [loading]);

  // ----- compute current layout (memoized) --------------------------------

  const listenerObjs = useMemo(() => agent?.listeners || [], [agent]);
  const skillObjs = useMemo(() => skills, [skills]);
  // Re-key the effect on the (id, custom?) tuple so toggling/marking custom
  // re-runs the layout — custom items drop out of defaultLayout placement.
  const listenerKey = useMemo(
    () => listenerObjs.map(l => `${l.id}:${isCustomListener(l) ? 1 : 0}`).join('|'),
    [listenerObjs],
  );
  const skillKey = useMemo(
    () => skillObjs.map(s => `${s.id}:${isCustomSkill(s) ? 1 : 0}`).join('|'),
    [skillObjs],
  );

  useEffect(() => {
    if (!agent) return;
    setLayout(prev => ensureLayout(
      agent.workflow_layout || prev, canvasSize.w, canvasSize.h, listenerObjs, skillObjs,
    ));
  }, [agent?.id, canvasSize.w, canvasSize.h, listenerKey, skillKey]);

  // ----- persistence (debounced) ------------------------------------------

  const saveTimerRef = useRef(null);
  const queuedRef = useRef(null);
  const persistLayout = useCallback((next) => {
    queuedRef.current = next;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      updateAgent(agentId, { workflow_layout: queuedRef.current }).catch(() => {});
    }, 400);
  }, [agentId]);

  // ----- node drag (move existing nodes around the canvas) -----------------

  const startMove = (kind, id) => (e) => {
    if (e.button !== 0) return;
    const pos = kind === 'agent' ? (layout.agent || agentPos) : layout[`${kind}s`][id];
    if (!pos) return;
    const rect = canvasRef.current.getBoundingClientRect();
    draggingRef.current = {
      kind, id,
      offsetX: e.clientX - rect.left - pos.x,
      offsetY: e.clientY - rect.top - pos.y,
    };
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = draggingRef.current;
      if (!d) return;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const w = d.kind === 'agent' ? AGENT_W : NODE_W;
      const h = d.kind === 'agent' ? AGENT_H : NODE_H;
      const x = Math.max(0, Math.min(rect.width - w, e.clientX - rect.left - d.offsetX));
      const y = Math.max(0, Math.min(rect.height - h, e.clientY - rect.top - d.offsetY));
      setLayout(prev => {
        let next;
        if (d.kind === 'agent') next = { ...prev, agent: { x, y } };
        else next = { ...prev, [`${d.kind}s`]: { ...prev[`${d.kind}s`], [d.id]: { x, y } } };
        persistLayout(next);
        return next;
      });
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [persistLayout]);

  // ----- palette drop (install + place a new node) ------------------------

  // Two flavours of drag from the palette:
  //   - `itemId`     -> a catalog template; drop will install a fresh instance
  //   - `existingId` -> an already-installed user-custom item that just needs
  //                     a position before it can show on the canvas
  const onPaletteDragStart = (kind, itemId, existingId) => (e) => {
    e.dataTransfer.effectAllowed = 'copy';
    const payload = existingId ? { kind, existingId } : { kind, itemId };
    e.dataTransfer.setData('text/aegent-node', JSON.stringify(payload));
  };

  const onCanvasDragOver = (e) => {
    if (e.dataTransfer.types.includes('text/aegent-node')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setHoverEdge('drop');
    }
  };

  const onCanvasDragLeave = () => setHoverEdge(null);

  const onCanvasDrop = async (e) => {
    e.preventDefault();
    setHoverEdge(null);
    const raw = e.dataTransfer.getData('text/aegent-node');
    if (!raw) return;
    const { kind, itemId, existingId } = JSON.parse(raw);
    const rect = canvasRef.current.getBoundingClientRect();
    const w = NODE_W, h = NODE_H;
    const x = Math.max(0, Math.min(rect.width - w, e.clientX - rect.left - w / 2));
    const y = Math.max(0, Math.min(rect.height - h, e.clientY - rect.top - h / 2));
    if (existingId) {
      // Promote an unplaced custom item: just assign it a position.
      const bucket = `${kind}s`; // listener->listeners, skill->skills, subagent->subagents
      const nextLayout = { ...layout, [bucket]: { ...layout[bucket], [existingId]: { x, y } } };
      setLayout(nextLayout);
      await updateAgent(agentId, { workflow_layout: nextLayout });
      setAgent(a => ({ ...a, workflow_layout: nextLayout }));
      return;
    }
    if (kind === 'listener') await installListener(itemId, { x, y });
    if (kind === 'skill') await installSkill(itemId, { x, y });
    if (kind === 'subagent') await installSubagentFromCatalog(itemId, { x, y });
  };

  // ----- install / remove --------------------------------------------------

  const installListener = async (itemId, position) => {
    const item = catalog.listeners.find(i => i.id === itemId);
    if (!item) return;
    setBusy({ kind: 'listener', id: itemId, action: 'install' });
    const listenerId = `${itemId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const defaultMode = (item.modes || []).find(m => m.default)?.id || item.modes?.[0]?.id || '';
    const spec = {
      id: listenerId,
      name: item.name,
      type: item.listener_type || 'polling',
      description: item.description || '',
      settings: { poll_interval: 60, mode: defaultMode },
      user_params: {},
      enabled: true,
      fromCatalog: true,
      catalog_item_id: itemId,
      mode: defaultMode,
      addedAt: new Date().toISOString(),
    };
    const nextListeners = [...(agent.listeners || []), spec];
    const nextLayout = { ...layout, listeners: { ...layout.listeners, [listenerId]: position } };
    setLayout(nextLayout);
    try {
      await updateAgent(agentId, { listeners: nextListeners, workflow_layout: nextLayout });
      await installListenerFromCatalog(agentId, listenerId, itemId);
      reloadService().catch(() => {});
      setAgent(a => ({ ...a, listeners: nextListeners, workflow_layout: nextLayout }));
    } finally {
      setBusy(null);
    }
  };

  const installSkill = async (itemId, position) => {
    const item = catalog.skills.find(i => i.id === itemId);
    if (!item) return;
    setBusy({ kind: 'skill', id: itemId, action: 'install' });
    try {
      const row = await installAgentSkill(agentId, { source: 'catalog', item_id: itemId });
      const sid = row?.id || itemId;
      const nextLayout = { ...layout, skills: { ...layout.skills, [sid]: position } };
      setLayout(nextLayout);
      const refreshed = await listAgentSkills(agentId);
      setSkills(refreshed);
      await updateAgent(agentId, { workflow_layout: nextLayout });
      setAgent(a => ({ ...a, workflow_layout: nextLayout }));
      reloadService().catch(() => {});
    } finally {
      setBusy(null);
    }
  };

  const installSubagentFromCatalog = async (itemId, position) => {
    setBusy({ kind: 'subagent', id: itemId, action: 'install' });
    try {
      // Install (ignore "already exists" — dragging an installed block should
      // just place it, not fail), then place based on the block that's
      // actually present so a fresh or pre-existing install both work.
      try {
        await installAgentSubagent(agentId, { source: 'catalog', item_id: itemId });
      } catch (err) {
        console.warn('subagent install returned an error (may already exist):', err);
      }
      const refreshed = await listAgentSubagents(agentId);
      setSubagents(refreshed);
      const found = refreshed.find(s => (s.slug || s.id) === itemId);
      if (!found) {
        console.error('subagent not present after install:', itemId);
        return;
      }
      const sid = found.slug || found.id;
      const nextLayout = { ...layout, subagents: { ...layout.subagents, [sid]: position } };
      setLayout(nextLayout);
      await updateAgent(agentId, { workflow_layout: nextLayout });
      setAgent(a => ({ ...a, workflow_layout: nextLayout }));
      reloadService().catch(() => {});
    } finally {
      setBusy(null);
    }
  };

  // Custom (user-authored) items — installed immediately so the agent owns them,
  // but left without a layout position. They surface in the palette as
  // draggable cards; dragging one onto the canvas is what places it.
  const createCustomListener = async ({ name, listenerType }) => {
    const slug = slugify(name);
    const listenerId = `${slug || 'custom'}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const settings = listenerType === 'polling' ? { poll_interval: 60 } : {};
    const spec = {
      id: listenerId,
      name: name || 'Custom Listener',
      type: listenerType,
      description: 'Custom user-authored listener.',
      settings,
      user_params: {},
      enabled: true,
      isCustom: true,
      addedAt: new Date().toISOString(),
    };
    const nextListeners = [...(agent.listeners || []), spec];
    setBusy({ kind: 'listener', id: listenerId, action: 'install' });
    try {
      await updateAgent(agentId, { listeners: nextListeners });
      await writeAgentListenerFile(agentId, listenerId, 'main.py', stubListenerCode(listenerType));
      setAgent(a => ({ ...a, listeners: nextListeners }));
      reloadService().catch(() => {});
      setCustomKind(null);
    } finally {
      setBusy(null);
    }
  };

  const createCustomSkill = async ({ name, description }) => {
    const slug = slugify(name) || `skill_${Math.random().toString(36).slice(2, 8)}`;
    setBusy({ kind: 'skill', id: slug, action: 'install' });
    try {
      await installAgentSkill(agentId, {
        source: 'blank', slug,
        name: name || slug,
        description: description || '',
        main: stubSkillCode(slug),
      });
      const refreshed = await listAgentSkills(agentId);
      setSkills(refreshed);
      reloadService().catch(() => {});
      setCustomKind(null);
    } finally {
      setBusy(null);
    }
  };

  const removeListener = async (id) => {
    const target = (agent.listeners || []).find(l => l.id === id);
    if (!target || target.isHeartbeat) return;
    const nextListeners = (agent.listeners || []).filter(l => l.id !== id);
    const { [id]: _, ...rest } = layout.listeners;
    const nextLayout = { ...layout, listeners: rest };
    setLayout(nextLayout);
    setAgent(a => ({ ...a, listeners: nextListeners, workflow_layout: nextLayout }));
    await updateAgent(agentId, { listeners: nextListeners, workflow_layout: nextLayout });
    reloadService().catch(() => {});
  };

  const removeSkill = async (id) => {
    setSkills(s => s.filter(x => x.id !== id));
    const { [id]: _, ...rest } = layout.skills;
    const nextLayout = { ...layout, skills: rest };
    setLayout(nextLayout);
    await uninstallAgentSkill(agentId, id).catch(() => {});
    await updateAgent(agentId, { workflow_layout: nextLayout }).catch(() => {});
    setAgent(a => ({ ...a, workflow_layout: nextLayout }));
    reloadService().catch(() => {});
  };

  // Subagents have no catalog, so (like custom skills) they're created blank,
  // installed immediately, and surface in the palette until dragged onto the
  // canvas. Editing happens in the dedicated Subagent workspace page.
  const createCustomSubagent = async ({ name, description }) => {
    const slug = slugify(name) || `subagent_${Math.random().toString(36).slice(2, 8)}`;
    setBusy({ kind: 'subagent', id: slug, action: 'install' });
    try {
      await installAgentSubagent(agentId, {
        source: 'blank', slug,
        name: name || slug,
        description: description || '',
      });
      const refreshed = await listAgentSubagents(agentId);
      setSubagents(refreshed);
      reloadService().catch(() => {});
      setCustomKind(null);
    } finally {
      setBusy(null);
    }
  };

  const removeSubagent = async (id) => {
    setSubagents(s => s.filter(x => (x.slug || x.id) !== id));
    const { [id]: _, ...rest } = layout.subagents;
    const nextLayout = { ...layout, subagents: rest };
    setLayout(nextLayout);
    await uninstallAgentSubagent(agentId, id).catch(() => {});
    await updateAgent(agentId, { workflow_layout: nextLayout }).catch(() => {});
    setAgent(a => ({ ...a, workflow_layout: nextLayout }));
    reloadService().catch(() => {});
  };

  // ----- connection anchors -----------------------------------------------

  // Until the layout-computing effect runs, fall back to a centred position so
  // the agent never flashes in the top-left corner on the first paint.
  const agentPos = layout.agent || {
    x: canvasSize.w / 2 - AGENT_W / 2,
    y: canvasSize.h / 2 - AGENT_H / 2,
  };

  // Custom items the user has created but not yet dragged onto the canvas.
  // They live in the palette as draggable cards; once dropped, a layout
  // position is assigned and they migrate to the canvas.
  const unplacedListeners = useMemo(
    () => (agent?.listeners || []).filter(l => !l.isHeartbeat && !layout.listeners[l.id]),
    [agent, layout.listeners],
  );
  const unplacedSkills = useMemo(
    () => skills.filter(s => !layout.skills[s.id]),
    [skills, layout.skills],
  );
  const unplacedSubagents = useMemo(
    () => subagents.filter(s => !layout.subagents[s.slug || s.id]),
    [subagents, layout.subagents],
  );

  const connections = useMemo(() => {
    if (!agent) return [];
    const out = [];
    const ax = agentPos.x;
    const ay = agentPos.y;
    const agentLeft = { x: ax, y: ay + AGENT_H / 2 };
    const agentRight = { x: ax + AGENT_W, y: ay + AGENT_H / 2 };

    for (const l of agent.listeners || []) {
      const p = layout.listeners[l.id];
      if (!p) continue;
      const from = { x: p.x + NODE_W, y: p.y + NODE_H / 2 };
      out.push({ key: `l-${l.id}`, d: curvePath(from.x, from.y, agentLeft.x, agentLeft.y), kind: 'in' });
    }
    for (const s of skills) {
      const p = layout.skills[s.id];
      if (!p) continue;
      const to = { x: p.x, y: p.y + NODE_H / 2 };
      out.push({ key: `s-${s.id}`, d: curvePath(agentRight.x, agentRight.y, to.x, to.y), kind: 'out' });
    }
    for (const s of subagents) {
      const sid = s.slug || s.id;
      const p = layout.subagents[sid];
      if (!p) continue;
      const agentCenterX = ax + AGENT_W / 2;
      const subagentCenterX = p.x + NODE_W / 2;
      const isAboveAgent = p.y + NODE_H / 2 < ay + AGENT_H / 2;
      const from = {
        x: agentCenterX,
        y: isAboveAgent ? ay : ay + AGENT_H,
      };
      const to = {
        x: subagentCenterX,
        y: isAboveAgent ? p.y + NODE_H : p.y,
      };
      out.push({
        key: `sa-${sid}`,
        d: verticalCurvePath(from.x, from.y, to.x, to.y),
        kind: 'out',
      });
    }
    return out;
  }, [agent, skills, subagents, layout]);

  // ----- node interactions -------------------------------------------------

  const handleNodeDoubleClick = (kind, id) => {
    if (kind === 'agent') {
      setOpenNode({ kind: 'agent', id: agentId });
    } else if (kind === 'listener') {
      const data = (agent.listeners || []).find(l => l.id === id);
      setOpenNode({ kind, id, data });
    } else if (kind === 'skill') {
      const data = skills.find(s => s.id === id);
      setOpenNode({ kind, id, data });
    } else if (kind === 'subagent') {
      const data = subagents.find(s => (s.slug || s.id) === id);
      setOpenNode({ kind, id, data });
    }
  };

  // ----- render ------------------------------------------------------------

  if (loading) {
    return <div className="wb-loading"><Loader2 size={20} className="wb-spin" /> Loading workflow…</div>;
  }
  if (!agent) {
    return <div className="wb-loading">Agent not found.</div>;
  }

  return (
    <div className="wb-root">
      {/* Top bar */}
      <TopNav agent={agent}>
        <button className="wb-icon-btn" onClick={toggleTheme} title="Toggle theme">
          {theme === 'dark' ? '○' : '●'}
        </button>
      </TopNav>

      <div className="wb-stage">
        {/* Left palette: listeners */}
        <Palette
          title="Listeners"
          subtitle="Drag onto canvas"
          items={catalog.listeners}
          customItems={unplacedListeners}
          kind="listener"
          onDragStart={onPaletteDragStart}
          onCreateCustom={() => setCustomKind('listener')}
        />

        {/* Canvas */}
        <div
          ref={canvasRef}
          className={`wb-canvas ${hoverEdge === 'drop' ? 'wb-canvas-drop' : ''}`}
          onDragOver={onCanvasDragOver}
          onDragLeave={onCanvasDragLeave}
          onDrop={onCanvasDrop}
        >
          {/* Grid background */}
          <div className="wb-canvas-grid" />

          {/* Connection lines (SVG layer) */}
          <svg className="wb-canvas-svg" width={canvasSize.w} height={canvasSize.h}>
            <defs>
              <linearGradient id="wbEdgeIn" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(99,102,241,0.35)" />
                <stop offset="100%" stopColor="rgba(99,102,241,0.9)" />
              </linearGradient>
              <linearGradient id="wbEdgeOut" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(34,197,94,0.9)" />
                <stop offset="100%" stopColor="rgba(34,197,94,0.35)" />
              </linearGradient>
            </defs>
            {connections.map(c => (
              <g key={c.key}>
                <path d={c.d} className="wb-edge-glow" stroke={c.kind === 'in' ? 'url(#wbEdgeIn)' : 'url(#wbEdgeOut)'} />
                <path d={c.d} className="wb-edge" stroke={c.kind === 'in' ? 'url(#wbEdgeIn)' : 'url(#wbEdgeOut)'} />
              </g>
            ))}
          </svg>

          {/* Agent node */}
          <AgentNode
            agent={agent}
            x={agentPos.x}
            y={agentPos.y}
            onPointerDown={startMove('agent')}
            onDoubleClick={() => handleNodeDoubleClick('agent')}
          />

          {/* Listener nodes */}
          {(agent.listeners || []).map(l => {
            const pos = layout.listeners[l.id]; if (!pos) return null;
            return (
              <FlowNode
                key={l.id}
                kind="listener"
                node={l}
                x={pos.x} y={pos.y}
                catalog={catalog}
                onPointerDown={startMove('listener', l.id)}
                onDoubleClick={() => handleNodeDoubleClick('listener', l.id)}
                onRemove={l.isHeartbeat ? null : () => removeListener(l.id)}
              />
            );
          })}

          {/* Skill nodes */}
          {skills.map(s => {
            const pos = layout.skills[s.id]; if (!pos) return null;
            return (
              <FlowNode
                key={s.id}
                kind="skill"
                node={s}
                x={pos.x} y={pos.y}
                catalog={catalog}
                onPointerDown={startMove('skill', s.id)}
                onDoubleClick={() => handleNodeDoubleClick('skill', s.id)}
                onRemove={() => removeSkill(s.id)}
              />
            );
          })}

          {/* Subagent nodes (double-click opens the dedicated workspace) */}
          {subagents.map(s => {
            const sid = s.slug || s.id;
            const pos = layout.subagents[sid]; if (!pos) return null;
            return (
              <FlowNode
                key={sid}
                kind="subagent"
                node={s}
                x={pos.x} y={pos.y}
                catalog={catalog}
                onPointerDown={startMove('subagent', sid)}
                onDoubleClick={() => handleNodeDoubleClick('subagent', sid)}
                onRemove={() => removeSubagent(sid)}
              />
            );
          })}

          {/* Empty-state hint */}
          {(agent.listeners || []).every(l => l.isHeartbeat) && skills.length === 0 && (
            <div className="wb-empty-hint">
              <Plus size={14} />
              Drag a listener from the left, then a skill from the right
            </div>
          )}
        </div>

        {/* Right rail: skills on top, subagents stacked underneath. Kept in a
            single grid column so the 3-column stage layout (and its heights)
            stay intact. */}
        <div className="wb-right-rail">
          <Palette
            title="Skills"
            subtitle="Drag onto canvas"
            items={catalog.skills}
            customItems={unplacedSkills}
            kind="skill"
            onDragStart={onPaletteDragStart}
            onCreateCustom={() => setCustomKind('skill')}
          />
          <Palette
            title="Subagents"
            subtitle="Drag onto canvas"
            items={catalog.subagents}
            customItems={unplacedSubagents}
            kind="subagent"
            onDragStart={onPaletteDragStart}
            onCreateCustom={() => setCustomKind('subagent')}
          />
        </div>
      </div>

      {busy && (
        <div className="wb-busy">
          <Loader2 size={13} className="wb-spin" /> {busy.action === 'install' ? `Installing ${busy.id}…` : 'Working…'}
        </div>
      )}

      <AnimatePresence>
        {openNode && (
          <NodeModal
            key={`${openNode.kind}-${openNode.id}`}
            node={openNode}
            agent={agent}
            agentId={agentId}
            catalog={catalog}
            skills={skills}
            subagents={subagents}
            onClose={() => setOpenNode(null)}
            onAgentChange={(patch) => setAgent(a => ({ ...a, ...patch }))}
            onListenerChange={(id, patch) => setAgent(a => ({
              ...a,
              listeners: (a.listeners || []).map(l => l.id === id ? { ...l, ...patch } : l),
            }))}
            onSkillChange={(id, patch) => setSkills(s => s.map(x => x.id === id ? { ...x, ...patch } : x))}
            onSubagentChange={(id, patch) => setSubagents(s => s.map(x => (x.slug || x.id) === id ? { ...x, ...patch } : x))}
          />
        )}
        {customKind && (
          <CustomCreator
            kind={customKind}
            onClose={() => setCustomKind(null)}
            onCreate={
              customKind === 'listener' ? createCustomListener
                : customKind === 'subagent' ? createCustomSubagent
                : createCustomSkill
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Palette (left = listeners, right = skills)
// ---------------------------------------------------------------------------

function Palette({ title, subtitle, items = [], customItems = [], kind, onDragStart, onCreateCustom }) {
  return (
    <aside className="wb-palette">
      <div className="wb-palette-head">
        <h3>{title}</h3>
        <span>{subtitle}</span>
      </div>
      <div className="wb-palette-body">
        {customItems.length > 0 && (
          <>
            <div className="wb-palette-section">Your custom</div>
            {customItems.map(item => {
              const Icon = kind === 'listener' ? Activity : kind === 'subagent' ? Boxes : Sparkles;
              const itemId = item.slug || item.id;
              return (
                <div
                  key={itemId}
                  className="wb-palette-item wb-palette-item-custom"
                  draggable
                  onDragStart={onDragStart(kind, null, itemId)}
                  title={item.description || `Custom ${kind}`}
                >
                  <span className="wb-palette-icon custom"><Icon size={14} /></span>
                  <span className="wb-palette-name">{item.name || itemId}</span>
                  <span className="wb-palette-tag">{kind === 'subagent' ? 'subagent' : 'custom'}</span>
                  <span className="wb-palette-grab">⋮⋮</span>
                </div>
              );
            })}
            <div className="wb-palette-section">Catalog</div>
          </>
        )}
        {items.map(item => {
          const Icon = kind === 'subagent' ? Boxes : iconFor(item.id);
          return (
            <div
              key={item.id}
              className="wb-palette-item"
              draggable
              onDragStart={onDragStart(kind, item.id)}
              title={item.description}
            >
              <span className="wb-palette-icon"><Icon size={14} /></span>
              <span className="wb-palette-name">{item.name}</span>
              <span className="wb-palette-grab">⋮⋮</span>
            </div>
          );
        })}
      </div>
      <div className="wb-palette-foot">
        <button className="wb-palette-custom" onClick={onCreateCustom}>
          <Plus size={13} /> Custom {kind}
        </button>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Canvas nodes
// ---------------------------------------------------------------------------

function AgentNode({ agent, x, y, onPointerDown, onDoubleClick }) {
  return (
    <div
      className="wb-node wb-agent"
      style={{ transform: `translate(${x}px, ${y}px)`, width: AGENT_W, height: AGENT_H }}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
    >
      <div className="wb-agent-ring" />
      <div className="wb-agent-body">
        <div className="wb-agent-icon"><Brain size={20} /></div>
        <div className="wb-agent-text">
          <div className="wb-node-name">{agent.name || 'Agent'}</div>
          <div className="wb-node-sub">double-click to edit</div>
        </div>
      </div>
    </div>
  );
}

function FlowNode({ kind, node, x, y, catalog, onPointerDown, onDoubleClick, onRemove }) {
  const isHeart = node.isHeartbeat || node.isInternalClock;
  const catItem = kind === 'listener'
    ? catalog.listeners.find(c => c.id === node.catalog_item_id)
    : kind === 'skill'
      ? catalog.skills.find(c => c.id === node.id || c.id === node.slug)
      : null;
  const Icon = isHeart ? Activity
    : kind === 'subagent' ? Boxes
    : iconFor(node.catalog_item_id || node.id || node.slug);
  // Subagents are spawned on demand -- always "on" (no enable/disable state).
  const enabled = kind === 'subagent' ? true : node.enabled !== false;
  const mode = node.mode || node.settings?.mode || node.metadata?.mode;
  const modeLabel = catItem && mode
    ? (catItem.modes || []).find(m => m.id === mode)?.label
    : null;
  const subLabel = modeLabel
    ? modeLabel
    : kind === 'listener' ? (node.type || 'listener')
    : kind === 'subagent'
      ? `${node.connections_count || 0} connection${node.connections_count === 1 ? '' : 's'}`
      : 'skill';

  return (
    <div
      className={`wb-node wb-${kind}-node ${enabled ? '' : 'wb-node-off'}`}
      style={{ transform: `translate(${x}px, ${y}px)`, width: NODE_W, height: NODE_H }}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
    >
      <div className={`wb-node-icon ${kind}`}><Icon size={14} /></div>
      <div className="wb-node-info">
        <div className="wb-node-name">{node.name || node.slug || node.id}</div>
        <div className="wb-node-sub">{subLabel}</div>
      </div>
      <div className="wb-node-status" data-on={enabled} />
      {onRemove && (
        <button
          className="wb-node-remove"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          onPointerDown={(e) => e.stopPropagation()}
          title="Remove"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal: settings / code (per node kind)
// ---------------------------------------------------------------------------

function NodeModal({ node, agent, agentId, catalog, skills, subagents, onClose, onAgentChange, onListenerChange, onSkillChange, onSubagentChange }) {
  const isAgent = node.kind === 'agent';
  const isListener = node.kind === 'listener';
  const isSkill = node.kind === 'skill';
  const isSubagent = node.kind === 'subagent';

  // Resolve catalog metadata + initial data shape per node kind
  const catItem = isListener
    ? catalog.listeners.find(c => c.id === node.data?.catalog_item_id)
    : isSkill
      ? catalog.skills.find(c => c.id === node.id || c.id === node.data?.slug)
      : null;
  const isCustom = !isAgent && !catItem;

  // Resolve the live copy of the node's data so the sub-panels see updates
  // made in this modal session (schema edits, saved rules, etc.). Falling
  // back to node.data keeps the modal usable if the parent hasn't yet
  // propagated a state update.
  const liveData = isListener
    ? (agent.listeners || []).find(l => l.id === node.id) || node.data
    : isSkill
      ? (skills || []).find(s => s.id === node.id) || node.data
      : isSubagent
        ? (subagents || []).find(s => (s.slug || s.id) === node.id) || node.data
        : node.data;

  // Which tabs make sense for this node:
  // - Code: shown unless the catalog item is a pass-through (no_code) with no
  //   user-editable logic (webhook, scheduled). Custom items always show code
  //   since the user wrote it. Agent always shows it (read-only prompt preview).
  // Credentials used to be a separate tab; they're now inlined under Settings.
  // Custom blocks always render the panel so users can declare fields.
  const showCode = isAgent || isCustom || !catItem?.no_code;
  const showCreds = isListener || isSkill;

  // Pick the requested tab if it's actually visible, otherwise the first one
  // that is. (Prevents a blank pane when the user opens a node whose
  // initialTab is hidden — e.g. a webhook listener wanting to open Code.)
  // Sidebar tab set per kind. Subagents get the full set; every other node
  // keeps the original Settings/Code pair. All render in the SAME sidebar
  // chrome so the modal is visually identical across kinds.
  const sideTabs = isSubagent
    ? [
        { id: 'settings', label: 'Settings', Icon: SettingsIcon },
        { id: 'credentials', label: 'Credentials', Icon: KeyRound },
        { id: 'setup', label: 'Setup', Icon: Rocket },
        { id: 'connections', label: 'Connections', Icon: Plug },
        { id: 'code', label: 'Code', Icon: Code2 },
        { id: 'memory', label: 'Memory', Icon: Brain },
      ]
    : [
        { id: 'settings', label: 'Settings', Icon: SettingsIcon },
        ...(showCode ? [{ id: 'code', label: 'Code', Icon: Code2 }] : []),
      ];
  const tabs = sideTabs.map((t) => t.id);
  const initial = tabs.includes(node.initialTab) ? node.initialTab : tabs[0];
  const [tab, setTab] = useState(initial);

  // Heading text + icon
  const HeadIcon = isAgent ? Brain
    : isSubagent ? Boxes
    : iconFor(node.data?.catalog_item_id || node.id);
  const heading = isAgent ? (agent.name || 'Agent')
    : (node.data?.name || node.data?.slug || node.id);

  return createPortal(
    <motion.div
      className="wb-modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="wb-modal"
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        onClick={e => e.stopPropagation()}
      >
        <aside className="wb-modal-side">
          <div className="wb-modal-side-head">
            <div className={`wb-modal-side-icon ${node.kind}`}><HeadIcon size={16} /></div>
            <div>
              <div className="wb-modal-title">{heading}</div>
              <div className="wb-modal-sub">{isAgent ? 'Agent' : isListener ? 'Listener' : isSubagent ? 'Subagent' : 'Skill'}</div>
            </div>
          </div>
          {sideTabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`wb-modal-tab ${tab === id ? 'active' : ''}`}
              onClick={() => setTab(id)}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
          <div className="wb-modal-side-spacer" />
          <button className="wb-modal-close" onClick={onClose}><X size={14} /> Close</button>
        </aside>

        <main className="wb-modal-main">
          {isSubagent ? (
            <SubagentConfig
              agentId={agentId}
              subId={node.id}
              activeTab={tab}
              onSubLoaded={(s) => onSubagentChange(node.id, {
                name: s.name,
                description: s.description,
                connections_count: s.connections_count,
              })}
            />
          ) : (
            <>
              {tab === 'settings' && (
                <div className="wb-settings-stack">
                  {isAgent && <AgentSettings agent={agent} agentId={agentId} onAgentChange={onAgentChange} />}
                  {isListener && (
                    <ListenerSettings
                      agent={agent} agentId={agentId} listener={liveData} catItem={catItem}
                      onChange={(patch) => onListenerChange(node.id, patch)}
                    />
                  )}
                  {isSkill && (
                    <SkillSettings
                      agentId={agentId} skill={liveData} catItem={catItem}
                      onChange={(patch) => onSkillChange(node.id, patch)}
                    />
                  )}
                  {showCreds && (
                    <CredentialsPanel
                      kind={node.kind} agent={agent} agentId={agentId}
                      target={liveData} catItem={catItem}
                      isCustom={isCustom}
                      onListenerChange={(patch) => onListenerChange(node.id, patch)}
                      onSkillChange={(patch) => onSkillChange(node.id, patch)}
                    />
                  )}
                </div>
              )}
              {tab === 'code' && (
                <CodePanel kind={node.kind} agent={agent} agentId={agentId} node={node} />
              )}
            </>
          )}
        </main>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Settings panels
// ---------------------------------------------------------------------------

function Field({ label, hint, children }) {
  return (
    <div className="wb-field">
      <label>{label}{hint && <span className="wb-field-hint"> · {hint}</span>}</label>
      {children}
    </div>
  );
}

function AgentSettings({ agent, agentId, onAgentChange }) {
  const [draft, setDraft] = useState({
    name: agent.name || '', role: agent.role || '', industry: agent.industry || '',
    objective: agent.objective || '', successDefinition: agent.successDefinition || '',
    doGuidelines: agent.doGuidelines || '', avoidGuidelines: agent.avoidGuidelines || '',
    escalationWhen: agent.escalationWhen || '',
    escalationChannel: agent.escalationChannel || 'none',
    escalationWho: agent.escalationWho || '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      const patch = { ...draft };
      await updateAgent(agentId, patch);
      onAgentChange(patch);
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    }, 600);
    return () => clearTimeout(t);
  }, [JSON.stringify(draft)]);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <div className="wb-pane">
      <div className="wb-pane-head">
        <h2>Identity & objective</h2>
        <span className={`wb-pane-save ${saved ? 'on' : ''}`}>{saved ? <><Check size={12} /> saved</> : 'auto-saving…'}</span>
      </div>
      <Field label="Name"><input value={draft.name} onChange={e => set('name', e.target.value)} /></Field>
      <div className="wb-row">
        <Field label="Role"><input value={draft.role} onChange={e => set('role', e.target.value)} /></Field>
        <Field label="Industry"><input value={draft.industry} onChange={e => set('industry', e.target.value)} /></Field>
      </div>
      <Field label="Objective" hint="What is the agent's job?">
        <textarea rows={3} value={draft.objective} onChange={e => set('objective', e.target.value)} />
      </Field>
      <Field label="Success looks like">
        <textarea rows={2} value={draft.successDefinition} onChange={e => set('successDefinition', e.target.value)} />
      </Field>
      <div className="wb-row">
        <Field label="Always">
          <textarea rows={3} value={draft.doGuidelines} onChange={e => set('doGuidelines', e.target.value)} />
        </Field>
        <Field label="Never">
          <textarea rows={3} value={draft.avoidGuidelines} onChange={e => set('avoidGuidelines', e.target.value)} />
        </Field>
      </div>
      <Field label="Escalate to a human when">
        <textarea rows={2} value={draft.escalationWhen} onChange={e => set('escalationWhen', e.target.value)} />
      </Field>
      <div className="wb-row">
        <Field label="Notify via" hint="How the human is reached">
          <select
            value={draft.escalationChannel}
            onChange={e => set('escalationChannel', e.target.value)}
          >
            {ESCALATION_CHANNELS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Field>
        <Field
          label={escalationTargetLabel(draft.escalationChannel)}
          hint={escalationTargetHint(draft.escalationChannel)}
        >
          <input
            value={draft.escalationWho}
            onChange={e => set('escalationWho', e.target.value)}
            disabled={draft.escalationChannel === 'none'}
            placeholder={escalationTargetPlaceholder(draft.escalationChannel)}
          />
        </Field>
      </div>
    </div>
  );
}

// Escalation helpers (label / placeholder / hint) live in ../escalation_meta
// so they stay identical across every UI surface that configures escalation.

function ListenerSettings({ agent, agentId, listener, catItem, onChange }) {
  const isHeart = listener.isHeartbeat;
  const listenerType = (listener.type || 'polling').toLowerCase();
  const isWebhook = listenerType === 'webhook';
  const modes = catItem?.modes || [];
  const currentMode = listener.mode || listener.settings?.mode || '';
  const [enabled, setEnabled] = useState(listener.enabled !== false);
  const [pollInterval, setPollInterval] = useState(listener.settings?.poll_interval ?? 60);
  const [mode, setMode] = useState(currentMode);
  const [name, setName] = useState(listener.name || '');
  const [description, setDescription] = useState(listener.description || '');
  const [informationText, setInformationText] = useState(
    listener.information_text || listener.informationText || '',
  );
  const [copied, setCopied] = useState(false);

  // The webhook endpoint is served by the backend at
  // POST /backend/webhook/<agent_id>/<listener_id>. We show the absolute URL so
  // the user can paste it into any upstream service (Stripe, GitHub, Zapier…).
  const webhookUrl = isWebhook
    ? `${window.location.origin}/backend/webhook/${agentId}/${listener.id}`
    : null;

  const copyUrl = async () => {
    if (!webhookUrl) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  const persist = async (patch) => {
    // `listener` is the open-time snapshot of node.data; read the live entry
    // from agent.listeners so concurrent edits in the same modal session don't
    // clobber each other.
    const live = (agent.listeners || []).find(l => l.id === listener.id) || listener;
    const nextSpec = { ...live, ...patch };
    const nextList = (agent.listeners || []).map(l => l.id === listener.id ? nextSpec : l);
    onChange(patch);
    await updateAgent(agentId, { listeners: nextList });
    reloadService().catch(() => {});
  };

  return (
    <div className="wb-pane">
      <div className="wb-pane-head"><h2>Listener settings</h2></div>

      {isWebhook && (
        <Field label="Webhook URL" hint="POST JSON here from any external service">
          <div className="wb-webhook-row">
            <code className="wb-webhook-url">{webhookUrl}</code>
            <button className="wb-btn ghost" onClick={copyUrl}>
              {copied ? <><Check size={13} /> Copied</> : 'Copy'}
            </button>
          </div>
        </Field>
      )}

      <Field label="Display name">
        <input value={name} onChange={e => setName(e.target.value)} onBlur={() => persist({ name })} />
      </Field>
      <Field label="Description" hint="What this listener does — surfaced to the agent">
        <textarea
          rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
          onBlur={() => persist({ description })}
          placeholder="e.g., Watches Gmail inbox for new customer replies"
        />
      </Field>
      {modes.length > 0 && (
        <Field label="Mode" hint={modes.find(m => m.id === mode)?.description}>
          <select value={mode} onChange={e => {
            setMode(e.target.value);
            persist({ mode: e.target.value, settings: { ...listener.settings, mode: e.target.value } });
          }}>
            {modes.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </Field>
      )}
      {!isHeart && !isWebhook && (listener.type || 'polling') === 'polling' && (
        <Field label="Poll interval (seconds)">
          <input
            type="number" min={5} value={pollInterval}
            onChange={e => setPollInterval(Number(e.target.value))}
            onBlur={() => persist({ settings: { ...listener.settings, poll_interval: pollInterval } })}
          />
        </Field>
      )}
      {listenerType === 'cron' && (
        <Field label="Schedule" hint="When this cron listener fires. Time is interpreted in the chosen timezone.">
          <SchedulePanel
            compact
            cronExpression={listener.cron_expression || listener.cronExpression || ''}
            cronTimezone={listener.cron_timezone || listener.cronTimezone || ''}
            onChange={(expr, tz) => {
              persist({
                cron_expression: expr, cronExpression: expr,
                cron_timezone: tz, cronTimezone: tz,
              });
            }}
          />
        </Field>
      )}
      <Field label="Enabled">
        <button
          className={`wb-toggle ${enabled ? 'on' : ''}`}
          onClick={() => { setEnabled(!enabled); persist({ enabled: !enabled }); }}
        >
          <Power size={12} /> {enabled ? 'On' : 'Off'}
        </button>
      </Field>
      <Field
        label={isHeart ? 'Scheduled rules' : 'Information & rules'}
        hint={isHeart
          ? 'Plain-English rules the heartbeat evaluates against the current time'
          : 'Free-form context, rules, or notes for the agent to read'}
      >
        <textarea
          rows={8}
          value={informationText}
          onChange={e => setInformationText(e.target.value)}
          onBlur={() => persist({
            information_text: informationText, informationText,
          })}
          placeholder={isHeart
            ? 'e.g.\n- Send me the weather forecast every day at 8:00 AM\n- Post a daily summary to Slack at 6:00 PM'
            : 'Additional context, filters, or behavioral rules for this listener'}
        />
      </Field>
      <div className="wb-pane-foot">
        Listener id: <code>{listener.id}</code>
      </div>
    </div>
  );
}

function SkillSettings({ agentId, skill, catItem, onChange }) {
  const modes = catItem?.modes || [];
  const isCustom = !catItem;
  const [mode, setMode] = useState(skill.mode || '');
  const [enabled, setEnabled] = useState(skill.enabled !== false);
  const [name, setName] = useState(skill.name || skill.id || '');
  const [description, setDescription] = useState(skill.description || skill.summary || '');
  const [busy, setBusy] = useState(false);

  const saveMode = async (m) => {
    setMode(m);
    setBusy(true);
    try {
      await setAgentSkillMode(agentId, skill.id, m);
      onChange({ mode: m });
    } finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) { await disableAgentSkill(agentId, skill.id); }
      else { await enableAgentSkill(agentId, skill.id); }
      setEnabled(!enabled);
      onChange({ enabled: !enabled });
    } finally { setBusy(false); }
  };

  // Custom skills store name/description in data.json — the backend
  // synthesizes the tool's identity from that file, so edits must round-trip
  // through it. Catalog skills keep their identity in the catalog and don't
  // expose these fields for editing.
  const saveDataJson = async (patch) => {
    try {
      let data = {};
      try {
        const cur = await readAgentSkillFile(agentId, skill.id, 'data.json');
        data = JSON.parse(cur?.content || '{}') || {};
      } catch { data = {}; }
      const next = { ...data, ...patch };
      await writeAgentSkillFile(agentId, skill.id, 'data.json', JSON.stringify(next, null, 2));
      onChange(patch);
    } catch {}
  };

  return (
    <div className="wb-pane">
      <div className="wb-pane-head"><h2>Skill settings</h2></div>
      {isCustom ? (
        <>
          <Field label="Name">
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={() => saveDataJson({ name })}
            />
          </Field>
          <Field label="Description" hint="What the agent should call this skill for">
            <textarea
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              onBlur={() => saveDataJson({ description, summary: description })}
              placeholder="e.g., Fetches the daily forecast for a given city"
            />
          </Field>
        </>
      ) : (
        <Field label="Name"><input value={skill.name || skill.id} disabled /></Field>
      )}
      {modes.length > 0 && (
        <Field label="Mode" hint={modes.find(m => m.id === mode)?.description}>
          <select value={mode} onChange={e => saveMode(e.target.value)} disabled={busy}>
            {modes.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </Field>
      )}
      <Field label="Enabled">
        <button className={`wb-toggle ${enabled ? 'on' : ''}`} onClick={toggle} disabled={busy}>
          <Power size={12} /> {enabled ? 'On' : 'Off'}
        </button>
      </Field>
      <div className="wb-pane-foot">Skill id: <code>{skill.id}</code></div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Credentials panel
// ---------------------------------------------------------------------------

function CredentialsPanel({ kind, agent, agentId, target, catItem, isCustom, onListenerChange, onSkillChange }) {
  // Schema can come from the catalog item (preferred) or from the spec itself.
  // Catalog blocks own their schema — the user just enters values; custom
  // blocks let the author declare fields inline via the schema editor below.
  const schema = useMemo(() => {
    if (catItem?.credentials && Array.isArray(catItem.credentials)) return catItem.credentials;
    if (Array.isArray(target?.credentials)) return target.credentials;
    return [];
  }, [catItem, target]);

  const [values, setValues] = useState(() => {
    if (kind === 'listener') {
      const merged = { ...(target?.user_params || {}) };
      const c = target?.credentials;
      if (c && !Array.isArray(c) && typeof c === 'object') Object.assign(merged, c);
      return merged;
    }
    return {};
  });
  const [loaded, setLoaded] = useState(kind === 'listener');
  const [savedFlash, setSavedFlash] = useState(false);
  const [test, setTest] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [editingSchema, setEditingSchema] = useState(false);
  const [schemaDraft, setSchemaDraft] = useState(() => schema.map(c => ({ ...c })));
  const [schemaSaving, setSchemaSaving] = useState(false);
  const [schemaError, setSchemaError] = useState('');
  useEffect(() => { setSchemaDraft(schema.map(c => ({ ...c }))); }, [schema]);

  const help = LISTENER_SETUP_HELP[catItem?.id]
    || (kind === 'listener' ? LISTENER_SETUP_HELP[listenerKind(target)] : undefined);

  useEffect(() => {
    if (kind === 'skill') {
      (async () => {
        try {
          const res = await getAgentSkillCredentials(agentId, target.id);
          setValues(res?.credentials || {});
        } catch {} finally { setLoaded(true); }
      })();
    }
  }, [kind, agentId, target?.id]);

  const set = (name, value) => setValues(v => ({ ...v, [name]: value }));

  // The skill creds API returns secrets as bullets (matching the stored
  // value's length) so a type="password" input renders them as dots and the
  // form looks like a normal "value is saved" field. We strip empty + bullet-
  // only entries before sending so untouched secrets don't round-trip back
  // and overwrite the real stored token.
  const isMask = (v) => typeof v === 'string' && v.length > 0 && /^•+$/.test(v);
  const filledValues = () => {
    const out = {};
    for (const [k, v] of Object.entries(values)) {
      if (v === '' || v === null || v === undefined) continue;
      if (isMask(v)) continue;
      out[k] = v;
    }
    return out;
  };

  const handleSave = async () => {
    if (kind === 'listener') {
      // `target` is the open-time snapshot; read the live entry from
      // agent.listeners so we don't overwrite changes made since the modal opened.
      const live = (agent.listeners || []).find(l => l.id === target.id) || target;
      const nextSpec = { ...live, user_params: values };
      const nextList = (agent.listeners || []).map(l => l.id === target.id ? nextSpec : l);
      await updateAgent(agentId, { listeners: nextList });
      onListenerChange({ user_params: values });
      reloadService().catch(() => {});
    } else {
      await putAgentSkillCredentials(agentId, target.id, { credentials: filledValues() });
      try {
        const res = await getAgentSkillCredentials(agentId, target.id);
        setValues(res?.credentials || {});
      } catch {}
    }
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1200);
  };

  // --- Schema editing (custom blocks only) --------------------------------
  // Listeners store the schema on the agent row's listener spec (spec.credentials).
  // Skills store it inside the skill's data.json on disk. Both surface it as
  // schema.credentials to the panel through the same read path above.
  const updateSchemaRow = (i, patch) => {
    setSchemaDraft(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  };
  const removeSchemaRow = (i) => {
    setSchemaDraft(rows => rows.filter((_, idx) => idx !== i));
  };
  const addSchemaRow = () => {
    setSchemaDraft(rows => [...rows, { name: '', label: '', type: 'text' }]);
  };
  const saveSchema = async () => {
    const cleaned = schemaDraft
      .map(r => ({
        name: (r.name || '').trim(),
        label: (r.label || '').trim() || (r.name || '').trim(),
        type: r.type || 'text',
      }))
      .filter(r => r.name);
    const seen = new Set();
    for (const r of cleaned) {
      if (seen.has(r.name)) {
        setSchemaError(`Duplicate field name: ${r.name}`);
        return;
      }
      seen.add(r.name);
    }
    setSchemaError('');
    setSchemaSaving(true);
    try {
      if (kind === 'listener') {
        await updateListenerSpec(agentId, target.id, { credentials: cleaned });
        onListenerChange && onListenerChange({ credentials: cleaned });
      } else {
        let data = {};
        try {
          const cur = await readAgentSkillFile(agentId, target.id, 'data.json');
          data = JSON.parse(cur?.content || '{}') || {};
        } catch { data = {}; }
        const next = { ...data, credentials: cleaned };
        await writeAgentSkillFile(agentId, target.id, 'data.json', JSON.stringify(next, null, 2));
        onSkillChange && onSkillChange({ credentials: cleaned });
      }
      setSchemaDraft(cleaned.map(c => ({ ...c })));
      setEditingSchema(false);
    } catch (e) {
      setSchemaError(e?.message || 'Failed to save schema');
    } finally {
      setSchemaSaving(false);
    }
  };

  const handleTest = async () => {
    if (!catItem) return;
    setTest({ state: 'testing' });
    try {
      const credsForTest = kind === 'listener' ? values : filledValues();
      const res = await testCatalogConnection({
        itemId: catItem.id, kind, credentials: credsForTest, agentId,
      });
      setTest({ state: res.ok ? 'ok' : 'fail', message: res.message || '' });
    } catch (e) {
      setTest({ state: 'fail', message: e.message || 'Test failed' });
    }
  };

  const schemaEditor = isCustom && (
    <div className="wb-schema-editor">
      <div className="wb-schema-editor-head">
        <div>
          <strong>Declared fields</strong>
          <div className="wb-field-hint">
            Fields the user will fill in below. Injected as env vars in <code>main.py</code>.
          </div>
        </div>
        {!editingSchema && (
          <button className="wb-btn ghost" onClick={() => setEditingSchema(true)}>
            {schema.length === 0 ? <><Plus size={13} /> Declare a field</> : 'Edit fields'}
          </button>
        )}
      </div>
      {editingSchema && (
        <>
          {schemaDraft.length === 0 && (
            <div className="wb-empty" style={{ marginBottom: 8 }}>
              No fields yet — click Add field to declare one.
            </div>
          )}
          {schemaDraft.map((row, i) => (
            <div key={i} className="wb-schema-row">
              <input
                placeholder="ENV_VAR_NAME"
                value={row.name || ''}
                onChange={e => updateSchemaRow(i, { name: e.target.value })}
              />
              <input
                placeholder="Label (shown to user)"
                value={row.label || ''}
                onChange={e => updateSchemaRow(i, { label: e.target.value })}
              />
              <select
                value={row.type || 'text'}
                onChange={e => updateSchemaRow(i, { type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="password">Password</option>
                <option value="textarea">Textarea</option>
              </select>
              <button
                className="wb-btn ghost"
                onClick={() => removeSchemaRow(i)}
                title="Remove field"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <div className="wb-cred-actions">
            <button className="wb-btn ghost" onClick={addSchemaRow}>
              <Plus size={13} /> Add field
            </button>
            <button className="wb-btn" onClick={saveSchema} disabled={schemaSaving}>
              {schemaSaving ? <Loader2 size={13} className="wb-spin" /> : <Save size={13} />} Save fields
            </button>
            <button
              className="wb-btn ghost"
              onClick={() => { setSchemaDraft(schema.map(c => ({ ...c }))); setEditingSchema(false); setSchemaError(''); }}
            >
              Cancel
            </button>
            {schemaError && <span className="wb-cred-fail"><AlertCircle size={13} /> {schemaError}</span>}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="wb-pane">
      <div className="wb-pane-head wb-pane-head-row">
        <h2>Credentials</h2>
        {help && (
          <button
            type="button"
            className="wb-btn ghost wb-help-btn"
            onClick={() => setShowHelp(true)}
            title={help.title}
          >
            <Info size={13} /> Setup guide
          </button>
        )}
      </div>
      {showHelp && help && (
        <div className="wb-modal-overlay" onClick={() => setShowHelp(false)} role="dialog" aria-modal="true">
          <div className="wb-modal wb-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wb-modal-side-head" style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <div className="wb-modal-title">{help.title}</div>
              <button className="wb-modal-close" onClick={() => setShowHelp(false)} style={{ marginLeft: 'auto' }}>
                <X size={14} /> Close
              </button>
            </div>
            <div className="wb-help-body listener-setup-help">{help.body}</div>
          </div>
        </div>
      )}
      {schemaEditor}
      {!loaded ? <div className="wb-empty">Loading…</div>
        : schema.length === 0 ? (
          <div className="wb-empty">
            {isCustom
              ? 'No fields declared yet. Use the editor above to add credentials this ' + kind + ' needs.'
              : `This ${kind} needs no credentials.`}
          </div>
        )
        : (
          <>
            {schema.map(c => (
              <Field key={c.name} label={c.label || c.name}>
                {c.type === 'textarea' ? (
                  <textarea
                    rows={3}
                    placeholder={c.placeholder || ''}
                    value={values[c.name] || ''}
                    onChange={e => set(c.name, e.target.value)}
                  />
                ) : (
                  <input
                    type={c.type === 'password' ? 'password' : 'text'}
                    placeholder={c.placeholder || ''}
                    value={values[c.name] || ''}
                    onChange={e => set(c.name, e.target.value)}
                  />
                )}
              </Field>
            ))}
            <div className="wb-cred-actions">
              <button className="wb-btn" onClick={handleSave}>
                <Save size={13} /> Save {savedFlash && <Check size={13} className="wb-cred-saved" />}
              </button>
              {catItem && (
                <button className="wb-btn ghost" onClick={handleTest} disabled={test?.state === 'testing'}>
                  {test?.state === 'testing' ? <Loader2 size={13} className="wb-spin" /> : 'Test connection'}
                </button>
              )}
              {test?.state === 'ok' && <span className="wb-cred-ok"><Check size={13} /> {test.message}</span>}
              {test?.state === 'fail' && <span className="wb-cred-fail"><AlertCircle size={13} /> {test.message}</span>}
            </div>
          </>
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Code panel (Monaco) — listeners edit main.py, skills edit main.py, agent
// shows a read-only generated system prompt.
// ---------------------------------------------------------------------------

function CodePanel({ kind, agentId, node, agent }) {
  const [content, setContent] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [transferError, setTransferError] = useState('');
  const folderInputRef = useRef(null);

  const path = 'main.py';
  // Zip download / folder upload only exist for listeners + skills.
  const hasTransfer = kind === 'listener' || kind === 'skill';

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (kind === 'listener') {
          const res = await readAgentListenerFile(agentId, node.id, path);
          if (alive) setContent(res?.content || '');
        } else if (kind === 'skill') {
          const res = await readAgentSkillFile(agentId, node.id, path);
          if (alive) setContent(res?.content || '');
        } else if (kind === 'agent') {
          const prompt = buildPromptPreview(agent);
          if (alive) setContent(prompt);
        }
      } catch (e) {
        if (alive) setContent(`# Failed to load: ${e.message || e}`);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [kind, agentId, node.id]);

  const save = async () => {
    if (kind === 'agent') return; // read-only preview
    setBusy(true);
    try {
      if (kind === 'listener') await writeAgentListenerFile(agentId, node.id, path, content);
      if (kind === 'skill') await writeAgentSkillFile(agentId, node.id, path, content);
      setDirty(false);
      reloadService().catch(() => {});
    } finally { setBusy(false); }
  };

  const handleDownloadZip = async () => {
    setDownloading(true);
    setTransferError('');
    try {
      if (kind === 'listener') await downloadAgentListenerZip(agentId, node.id);
      else if (kind === 'skill') await downloadAgentSkillZip(agentId, node.id);
    } catch (e) {
      setTransferError(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleUploadFolder = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (folderInputRef.current) folderInputRef.current.value = '';
    if (!picked.length) return;
    setUploading(true);
    setTransferError('');
    try {
      if (kind === 'listener') {
        await uploadAgentListenerFolder(agentId, node.id, picked, { stripTop: true });
      } else if (kind === 'skill') {
        await uploadAgentSkillFolder(agentId, node.id, picked, { stripTop: true });
      }
      const readFn = kind === 'listener' ? readAgentListenerFile : readAgentSkillFile;
      const res = await readFn(agentId, node.id, path);
      setContent(res?.content || '');
      setDirty(false);
      reloadService().catch(() => {});
    } catch (err) {
      setTransferError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="wb-pane wb-pane-code">
      <div className="wb-pane-head">
        <h2>{kind === 'agent' ? 'System prompt preview' : path}</h2>
        {kind !== 'agent' && (
          <div className="wb-pane-actions">
            {hasTransfer && (
              <>
                <button
                  className="wb-btn"
                  onClick={handleDownloadZip}
                  disabled={downloading}
                  title={`Download this ${kind} as a .zip`}
                >
                  {downloading ? <Loader2 size={13} className="wb-spin" /> : <Download size={13} />} Zip
                </button>
                <button
                  className="wb-btn"
                  onClick={() => folderInputRef.current?.click()}
                  disabled={uploading}
                  title="Upload a folder of files (overwrites by path)"
                >
                  {uploading ? <Loader2 size={13} className="wb-spin" /> : <FolderUp size={13} />} {uploading ? 'Uploading…' : 'Upload'}
                </button>
                <input
                  ref={folderInputRef}
                  type="file"
                  multiple
                  webkitdirectory=""
                  directory=""
                  style={{ display: 'none' }}
                  onChange={handleUploadFolder}
                />
              </>
            )}
            <button className="wb-btn" onClick={save} disabled={!dirty || busy}>
              {busy ? <Loader2 size={13} className="wb-spin" /> : <Save size={13} />} Save
              {dirty && !busy && <span className="wb-dirty-dot" />}
            </button>
          </div>
        )}
      </div>
      {transferError && <div className="wb-pane-error">{transferError}</div>}
      <div className="wb-code-wrap">
        {!loaded ? <div className="wb-empty">Loading…</div> : (
          <Editor
            height="100%"
            theme="vs-dark"
            language={kind === 'agent' ? 'markdown' : 'python'}
            value={content}
            onChange={(v) => { setContent(v || ''); setDirty(true); }}
            options={{
              minimap: { enabled: false }, fontSize: 12.5,
              fontFamily: "'SF Mono', 'Monaco', monospace",
              scrollBeyondLastLine: false, automaticLayout: true,
              wordWrap: 'on', tabSize: 4, renderLineHighlight: 'gutter',
              readOnly: kind === 'agent',
            }}
          />
        )}
      </div>
    </div>
  );
}

function buildPromptPreview(agent) {
  // Best-effort mirror of agent/runtime.py::_instructions so the user sees what
  // the runtime will actually feed the model. Kept simple — the source of truth
  // is the backend; this is just a preview pane.
  const L = [];
  L.push('You are an autonomous operations agent acting on behalf of a company.');
  if (agent.role) L.push(`Your role: ${agent.role}.`);
  if (agent.industry) L.push(`Industry: ${agent.industry}.`);
  if (agent.objective) L.push('', 'Objective:', agent.objective);
  if (agent.successDefinition) L.push('', 'Success looks like:', agent.successDefinition);
  if (agent.doGuidelines) L.push('', 'Always:', agent.doGuidelines);
  if (agent.avoidGuidelines) L.push('', 'Never:', agent.avoidGuidelines);
  if (agent.escalationWhen) L.push('', `Escalate to a human when: ${agent.escalationWhen}`);
  L.push('', 'You are triggered by an inbound event from one of your listeners. Work in two steps:');
  L.push('1. RELEVANCE: Decide whether this event is something you should act on …');
  L.push('2. ACTION: If it is relevant, handle it end to end with your tools …');
  return L.join('\n');
}

// ---------------------------------------------------------------------------
// Custom creator (small dialog — name + optional listener type, then opens the
// new node in the main modal on the Code tab so the user can start editing).
// ---------------------------------------------------------------------------

function CustomCreator({ kind, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [listenerType, setListenerType] = useState('polling');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const isListener = kind === 'listener';

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isListener) await onCreate({ name: name.trim(), listenerType });
      else await onCreate({ name: name.trim(), description: description.trim() });
    } catch (e) {
      setBusy(false);
    }
  };

  return createPortal(
    <motion.div
      className="wb-modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="wb-custom"
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="wb-custom-head">
          <div className={`wb-custom-icon ${kind}`}>
            {isListener ? <Activity size={16} /> : kind === 'subagent' ? <Boxes size={16} /> : <Sparkles size={16} />}
          </div>
          <div>
            <h2>New custom {kind}</h2>
            <p>Starts from a working stub — open the Code tab to flesh it out.</p>
          </div>
          <button className="wb-custom-close" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="wb-custom-body">
          <Field label="Name" hint="Used for the node label">
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={isListener ? 'e.g., Stripe webhook' : 'e.g., Refund order'}
              onKeyDown={e => { if (e.key === 'Enter' && name.trim()) submit(); }}
            />
          </Field>

          {isListener && (
            <Field label="Type" hint="How the listener is triggered">
              <select value={listenerType} onChange={e => setListenerType(e.target.value)}>
                <option value="polling">Polling — runs on a timer</option>
                <option value="webhook">Webhook — fires on incoming HTTP POST</option>
                <option value="cron">Scheduled — fires on a schedule</option>
              </select>
            </Field>
          )}

          {!isListener && (
            <Field label="Description" hint="Tells the agent when to reach for it">
              <textarea
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={kind === 'subagent'
                  ? 'What this subagent does, in one sentence.'
                  : 'What this skill does, in one sentence.'}
              />
            </Field>
          )}
        </div>

        <div className="wb-custom-foot">
          <button className="wb-btn ghost" onClick={onClose}>Cancel</button>
          <button className="wb-btn" onClick={submit} disabled={!name.trim() || busy}>
            {busy ? <><Loader2 size={13} className="wb-spin" /> Creating…</> : <>Create {kind}</>}
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
